<template>
  <b-container class="referral-jam-event-page mb-5">
    <div class="d-flex justify-content-center align-items-center my-5" v-if="state === 'LOADING'">
      <b-spinner type="grow" variant="primary"/>
    </div>
    <div class="d-flex justify-content-center align-items-center my-5"  v-else-if="state === 'NOT-FOUND'">
      <p>Event not found.</p>
    </div>
    <template v-else-if="event && event.id">
      <div class="d-flex flex-row justify-content-start align-items-start my-3 border-bottom pb-3">
        <img class="event-logo mr-3" v-if="event.branding && event.branding.logo && event.branding.logo.fileLocation"
          :src="event.branding.logo.fileLocation"/>
        <h2 class="m-0">{{ event.title }}</h2>
        <div class="flex-fill"/>
        <b-alert class="m-2 m-md-0" show variant="info" v-if="event.endDate && event.endDate < timeNow">
          <strong>Ended at:</strong> {{ event.endDate | formatDate('Do MMMM YYYY, HH:mm') }}
        </b-alert>
        <b-alert class="m-2 m-md-0" show variant="info" v-else-if="event.startDate && event.endDate && event.startDate < timeNow">
          <strong>Ends in:</strong> {{ eventEndsIn }}
        </b-alert>
        <b-alert class="d-none d-lg-block m-2 m-md-0" show variant="info" v-else-if="event.startDate > timeNow">
          <strong>Starts in:</strong> {{ eventStartsIn }}
        </b-alert>
        <b-dropdown v-if="isRecruiter" no-caret right variant="transparent">
          <template #button-content><font-awesome-icon icon="ellipsis-v"/></template>
          <b-dropdown-item :to="{ name: 'EventRoom', params: { eventID: event.id } }">View Room</b-dropdown-item>
        </b-dropdown>
      </div>

      <b-alert variant="warning" show v-if="event.status === 'INACTIVE'">
        <strong>This event has been disabled</strong> - <router-link :to="{ name: 'EventSettings', params: { eventID: event.id } }">Edit settings</router-link>
      </b-alert>
      <b-alert variant="danger" show v-if="event.status === 'DELETED'">
        <strong>This event has been deleted</strong> - <router-link :to="{ name: 'EventSettings', params: { eventID: event.id } }">Edit settings</router-link>
      </b-alert>

      <b-row>
        <b-col cols="12" lg="8">
          <b-media class="m-3 align-items-center" v-if="event.leaderboard && event.leaderboard.prizes && event.leaderboard.prizes.text">
            <template #aside v-if="event.leaderboard.prizes && event.leaderboard.prizes.first && event.leaderboard.prizes.first.image && typeof event.leaderboard.prizes.first.image.fileLocation === 'string'">
              <b-img v-if="event.leaderboard.prizes.first.image.fileLocation.endsWith('.gif')" class="rounded-circle" height="100" width="100"
                :src="event.leaderboard.prizes.first.image.fileLocation" :alt="event.leaderboard.prizes.first.title || null"/>
              <b-img v-else class="rounded-circle" height="100" width="100"
                :src="event.leaderboard.prizes.first.image.fileLocation | cdn('resize', 256, 256)" :alt="event.leaderboard.prizes.first.title || null"/>
            </template>
            <h3 class="m-0">{{ event.leaderboard.prizes.text }}</h3>
          </b-media>
          <rich-text-content v-if="event.pageContent" class="my-5" :value="event.pageContent"/>
        </b-col>
        <b-col cols="12" lg="4" class="my-3 my-lg-0">
          <b-card class="my-3">
            <template v-if="eventUserParticipant">
              <h4 class="border-bottom pb-2 text-uppercase">Signed Up</h4>
              <div class="d-flex my-3 flex-row justify-content-start align-items-center">
                <avatar class="mr-3" :user="user" :size="45"/>
                <p class="m-0"><strong>{{user.fullName}}</strong>
                  <br/>{{ eventUserParticipant.createdAt | formatDateRelative({ addSuffix: true }) }}</p>
              </div>

              <b-btn class="text-left w-100" :to="joinLink" variant="primary" v-if="event.endDate && event.endDate < timeNow">
                View event
              </b-btn>
              <b-btn class="text-left w-100" disabled variant="primary" v-else-if="event.startDate && event.startDate > timeNow">
                This event has not started yet
              </b-btn>
              <b-btn class="text-left w-100" :to="joinLink" variant="primary" v-else>
                Join event
              </b-btn>
            </template>
            <template v-else-if="user && user.id">
              <h4 class="border-bottom pb-2 text-uppercase">Sign Up</h4>
              <div class="d-flex my-3 flex-row justify-content-start align-items-center">
                <avatar class="mr-3" :user="user" :size="45"/>
                <p class="m-0"><strong>{{user.fullName}}</strong></p>
              </div>

              <b-btn class="text-left w-100" @click="addAsEventParticipant" variant="primary">
                Join Event
              </b-btn>
            </template>
            <template v-else>
              <h4 class="border-bottom pb-2 text-uppercase">Sign Up</h4>
              <b-btn class="border text-left w-100" :to="signinWithEmailURL" variant="light">
                <font-awesome-icon class="mr-2" icon="envelope" size="lg"/>
                Sign in with email/password
              </b-btn>
            </template>
          </b-card>

          <!-- <b-card class="my-3" v-if="user && user.id && canSyncContacts">
            <h4 class="border-bottom pb-2 text-uppercase">Sync Contacts</h4>
            <p>Sync your contacts to get matches for vacancies in this Referral Jam.</p>

            <b-btn v-if="event.endDate && event.endDate < timeNow" class="border text-left w-100" disabled variant="light">
              This event has ended
            </b-btn>
            <b-btn v-else class="border text-left w-100" :to="{ name: 'EventSyncContacts', params: { eventID } }" variant="light">
              <font-awesome-icon class="mr-2" icon="users" size="lg"/>
              Sync your contacts
            </b-btn>
          </b-card> -->

          <b-card class="my-3" v-if="event.startDate && event.endDate && !eventPreferences.hideSaveToCalendar"
            body-class="d-flex flex-row justify-content-between align-items-center">
              <h4 class="text-uppercase m-0 py-2">Save to Calendar</h4>
              <add-to-calendar :event="event"/>
          </b-card>

          <b-card class="my-3" v-if="!eventPreferences.hideParticipantsList">
            <h4 class="border-bottom pb-2 text-uppercase">Participants</h4>
            <participants-list v-if="Array.isArray(eventParticipants) && eventParticipants.length"
              :participants="eventParticipants"/>
            <p v-else class="m-0">As participants register for the event, they'll appear here</p>
          </b-card>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
import cdnTransform from '@/filters/cdn';
import qs from 'querystring';
import AuthEventSignupBySocialMutation from '@src/graphql/mutations/AuthEventSignupBySocial.gql';
import RfjmAddParticipantsMutation from '@src/graphql/mutations/RfjmAddParticipants.gql';
import Avatar from '@src/components/users/Avatar';
import AddToCalendar from '@src/components/referral-jam/AddToCalendar';
import EventPageQuery from '@/graphql/queries/RfjmEventPage.gql';
import ParticipantsList from '@src/components/referral-jam/ParticipantsList';
import authMixin from '@src/mixins/auth';
import eventsMixin from '@src/mixins/events';
import { RichTextContent } from '@/components/RichTextField';
import { createVueMetaBranding } from '@src/lib/branding';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    eventID: {
      type: String,
      required: true,
    },
  },
  components: {
    Avatar,
    AddToCalendar,
    ParticipantsList,
    RichTextContent,
  },
  mixins: [
    eventsMixin,
  ],
  data: () => ({
    state: null,
  }),
  created() {
    this.state = 'LOADING';
  },
  apollo: {
    event: {
      query: EventPageQuery,
      variables() {
        const eventID = this.eventID.split('-').pop();
        return { eventID };
      },
      update(data) {
        const { company } = data || {};
        const { event } = company || {};
        return event;
      },
      async result({ loading, data }) {
        if (!loading && data && data.company && data.company.event) {
          const { status } = data.company.event;

          if (this.eventID !== this.eventHandle) {
            this.$router.replace({
              ...this.$route,
              params: { ...this.$route.params, eventID: this.eventHandle },
            });
          }

          if (status === 'ACTIVE' || this.isRecruiterAdmin) {
            this.state = 'READY';
          } else {
            this.state = 'NOT-FOUND';
          }

          if (process.env.VUE_ENV === 'client') {
            if (this.state === 'READY' && this.$route.query && this.$route.query.callback) {
              try {
                switch (this.$route.query.callback) {
                  case 'signinWithEmail': {
                    await this.addAsEventParticipant();
                    break;
                  }
                  case 'signInWithLinkedIn': {
                    await this.onSocialAuthCallback('linkedin');
                    break;
                  }
                }
              } catch (err) {
                this.$log.error(err);
              } finally {
                const { callback, ...query } = this.$route.query;
                this.$router.replace({ ...this.$route, query });
                window.location.hash = '';
              }
            }
          }
        } else if (!loading && data && data.company) {
          this.state = 'NOT-FOUND';
        }
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      prefetch: true,
    },
  },
  metaInfo() {
    const canonical = `${this.$root.origin}${this.$route.fullPath}`;

    const head = {
      title: null,
      meta: [],
      link: [ { vmid: 'canonical', rel: 'canonical', href: canonical } ],
      script: [],
    };

    if (this.company) {
      const companyName = (this.company.siteBranding && this.company.siteBranding.name) || this.company.name;

      if (companyName) {
        head.meta.push({ vmid: 'author', name: 'author', content: companyName });
        head.meta.push({ vmid: 'og:site_name', property: 'og:site_name', content: companyName });
      }
    }

    if (this.event && this.event.id) {
      head.meta.push({ vmid: 'og:url', property: 'og:url', content: canonical });

      if (this.event.title) {
        head.title = this.event.title;
        head.meta.push({ vmid: 'og:title', property: 'og:title', content: this.event.title });
        head.meta.push({ vmid: 'twitter:title', property: 'twitter:title', content: this.event.title });
      }

      if (this.event.branding) {
        const isAdminUI = process.env.VUE_ENV === 'server' ? false : process.env.ADMIN_UI;
        const nonce = process.env.VUE_ENV === 'server' ? (this.$ssrContext.nonce || '') : '';
        createVueMetaBranding.call(this, head, this.event.branding, { isAdminUI, nonce });
      }

      if (this.event.branding && this.event.branding.socialImage && this.event.branding.socialImage.fileLocation) {
        const { fileLocation } = this.event.branding.socialImage;
        const content = `${this.$root.origin}${cdnTransform(fileLocation, 'resize', 1200, 627)}`;
        head.meta.push({
          vmid: 'twitter:image',
          name: 'twitter:image',
          content,
        });
        head.meta.push({
          vmid: 'og:image',
          property: 'og:image',
          content,
        });
      } else if (this.company && this.company.siteBranding && this.company.siteBranding.socialImage && this.company.siteBranding.socialImage.fileLocation) {
        head.meta.push({
          vmid: 'twitter:image',
          name: 'twitter:image',
          content: this.company.siteBranding.socialImage.fileLocation,
        });
        head.meta.push({
          vmid: 'og:image',
          property: 'og:image',
          content: this.company.siteBranding.socialImage.fileLocation,
        });
      }

      // article:modified_time -> updated at?
      // article:expiration_time -> expiry time?
    }

    return head;
  },
  computed: {
    ...mapRoot([ 'user', 'isRecruiter', 'isRecruiterAdmin', 'isDemoSite', 'isEventsSite' ]),
    signinWithLinkedInURL() {
      const { authServiceOrigin, origin } = this.$root;
      const { path: currentPath } = this.$route;
      return ((p, q) => `${authServiceOrigin}${p}?${qs.stringify(q)}`)('/oauth2/linkedin', {
        scope: 'r_liteprofile r_emailaddress',
        return_url: `${origin}${currentPath}?callback=signInWithLinkedIn`,
      });
    },
    signinWithEmailURL() {
      const { path: currentPath } = this.$route;
      return `/login?next=${encodeURIComponent(`${currentPath}?callback=signinWithEmail`)}`;
    },
    joinLink() {
      const { eventID } = this;
      const { welcomeContent } = this.event || {};
      return {
        name: welcomeContent ? 'EventWelcome' : 'EventRoom',
        params: { eventID },
      };
    },
  },
  methods: {
    async onSocialAuthCallback(provider) {
      const { id: eventID } = this.event || {};

      const { hash: hashString } = this.$route;
      const routeHash = qs.parse((hashString || '').substr(1));
      this.$log.debug(routeHash);

      if (routeHash.access_token) {
        const { updateAuthResponse } = authMixin.methods;
        const { access_token: accessToken } = routeHash;

        this.$assert(eventID, 'Missing eventID');

        const { errors } = await this.$apollo.mutate({
          mutation: AuthEventSignupBySocialMutation,
          variables: { eventID, provider, accessToken },
          update: (client, { data: { eventSignupBySocial } }) => {
            const { auth: authResponse } = eventSignupBySocial;
            // @TODO Add participant to the participant list using this response data
            updateAuthResponse.call(this, client, authResponse);
          },
          errorPolicy: 'all',
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('AuthEventSignupBySocialMutation', err));
          throw errors.shift();
        }

        this.$assert(this.isLoggedIn, new Error('Failed to login by social'));
        // Reload the participants list!
        this.$apollo.queries.event.refetch();
      }
    },
    async addAsEventParticipant() {
      try {
        const { id: eventID } = this.event || {};
        this.$assert(eventID, 404, 'Event not found');
        this.$assert(this.user && this.user.id, 401, 'You are not signed-in');

        // Attempt to login by identity
        const { errors } = await this.$apollo.mutate({
          mutation: RfjmAddParticipantsMutation,
          variables: { event: { id: eventID }, users: { self: true } },
          errorPolicy: 'all',
        });

        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('RfjmAddParticipantsMutation', err));
          throw errors.shift();
        }

        if (this.eventHasStarted) {
          this.$router.push(this.joinLink);
        } else {
          // Trigger a reload for the Event's participants list
          Promise.resolve(this.$apollo.queries.event.refetch()).catch(err => this.$log.error(err));

          this.$notification({
            title: 'Signed up!',
            message: 'You have successfully registered for this event',
            variant: 'success',
          });
        }
      } catch (err) {
        this.$notifyErr(err);
      }
    },
  },
};
</script>

<style lang="scss">
.referral-jam-event-page {
  .event-logo {
    max-height: 30px;
    max-width: 200px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ReferralJam/EventPage.vue